import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import {
  BackgroundJob,
  TaskTimelineCompletionGroupByEnum,
} from 'graphql-common';
import toast from '@lib/components/Toast/Toast';
import { Values } from '@lib/interfaces/form';
import { EventBusEvents, jobEventBus } from '@lib/utils/eventBus';
import { FieldNames, FormTypeValue } from 'components/GenerateReportForm/types';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import getPreparedReportDataFromValues from './getPreparedReportDataFromValues';

type Args = {
  basePeriodEndParam: string;
  basePeriodStartParam: string;
  completionGroupBy: TaskTimelineCompletionGroupByEnum;
  filterParams: Values;
  onCompleted: () => void;
  taskTimelineReportCreateMutation: (v: object) => Promise<unknown>;
  taskIds?: string[];
};

export default function useGenerateReportActions(args: Args) {
  const {
    completionGroupBy,
    filterParams,
    taskIds = [],
    onCompleted,
    taskTimelineReportCreateMutation,
    basePeriodStartParam,
    basePeriodEndParam,
  } = args;
  const { t } = useTranslation();

  const onReportCreateAction = (values: Values) => {
    const formType = _get(values, [FieldNames.FormType, 'value']);
    const params = getPreparedReportDataFromValues({
      completionGroupBy,
      values,
      taskIds,
      filterParams,
      basePeriodStartParam,
      basePeriodEndParam,
    });
    taskTimelineReportCreateMutation({
      variables: { input: { params } },
      onCompleted,
    })
      .then((response) => {
        const backgroundJob = _get(response, 'data.data') as
          BackgroundJob | undefined;
        if (backgroundJob) {
          jobEventBus.emit(EventBusEvents.JobCreated, backgroundJob);
        }
        if (formType === FormTypeValue.Download) {
          toast({ content: t('get-tasks-report-download-success') });
        } else if (formType === FormTypeValue.Email) {
          toast({ content: t('get-tasks-report-email-success') });
        }
      })
      .catch((error) => {
        const message = _get(error, 'message');
        let scopeMessage;
        getGraphQLErrors(error)?.forEach((errorItem) => {
          const details = errorItem?.extensions?.details;
          const errorMessageByScope = Array.isArray(details)
            ? details.find(({ path }) => path[0] === 'scope')?.message
            : undefined;
          if (errorMessageByScope) scopeMessage = errorMessageByScope;
        });
        const content = scopeMessage || message || t('something-went-wrong');
        toast({ content });
      });
  };

  return {
    onReportCreateAction,
  };
}
